/**
 * Unified multi-platform tracking dispatcher
 * Dispatches events to: GA4, Meta Pixel, TikTok Pixel, LinkedIn Insight Tag, GHL
 */

// ---------------------------------------------------------------------------
// Type declarations for global pixel objects
// ---------------------------------------------------------------------------
declare global {
  interface Window {
    gtag?: (...args: any[]) => void
    dataLayer: any[]
    fbq?: (...args: any[]) => void
    ttq?: { track: (...args: any[]) => void; page: () => void; identify: (params: Record<string, any>) => void }
    lintrk?: (action: string, params?: Record<string, any>) => void
    _linkedin_data_partner_ids?: string[]
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function ga4(event: string, params?: Record<string, any>) {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', event, params)
  }
}

function meta(event: string, params?: Record<string, any>, custom = false) {
  if (typeof window !== 'undefined' && window.fbq) {
    if (custom) {
      window.fbq('trackCustom', event, params)
    } else {
      window.fbq('track', event, params)
    }
  }
}

function tiktok(event: string, params?: Record<string, any>) {
  if (typeof window !== 'undefined' && window.ttq) {
    window.ttq.track(event, params)
  }
}

function linkedin(conversionId?: number) {
  if (typeof window !== 'undefined' && window.lintrk && conversionId) {
    window.lintrk('track', { conversion_id: conversionId })
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function trackPageView(path: string, title: string) {
  ga4('page_view', { page_path: path, page_title: title })
  // Meta & TikTok fire PageView automatically via their SDK init
  // LinkedIn fires automatically via the Insight Tag
}

export function trackCTAClick(ctaName: string, destination: string) {
  ga4('cta_click', { cta_name: ctaName, destination })
  meta('ClickButton', { cta_name: ctaName, destination }, true)
  tiktok('ClickButton', { content_name: ctaName })
}

export function trackConversion(
  type: 'lead' | 'purchase' | 'checkout_begin' | 'add_payment_info',
  value?: number,
  currency = 'USD'
) {
  const valueParams = value ? { value, currency } : undefined

  switch (type) {
    case 'lead':
      ga4('generate_lead', valueParams)
      meta('Lead', valueParams)
      tiktok('SubmitForm', valueParams)
      linkedin() // conversion ID set in LinkedIn Campaign Manager
      break
    case 'purchase':
      ga4('purchase', valueParams)
      meta('Purchase', valueParams)
      tiktok('CompletePayment', valueParams)
      linkedin()
      break
    case 'checkout_begin':
      ga4('begin_checkout', valueParams)
      meta('InitiateCheckout', valueParams)
      tiktok('InitiateCheckout', valueParams)
      break
    case 'add_payment_info':
      ga4('add_payment_info', valueParams)
      meta('AddPaymentInfo', valueParams)
      tiktok('AddPaymentInfo', valueParams)
      break
  }
}

export function trackCalendlyClick(callType: string) {
  ga4('calendly_click', { call_type: callType })
  meta('Schedule', { call_type: callType })
  tiktok('Contact', { content_name: callType })
  linkedin()
}

export function trackAssessmentEvent(
  action: 'started' | 'completed' | 'unlocked',
  assessmentName: string,
  score?: number
) {
  ga4(`assessment_${action}`, { assessment_name: assessmentName, score })

  switch (action) {
    case 'unlocked':
      meta('ViewContent', { content_name: assessmentName }, true)
      tiktok('ViewContent', { content_name: assessmentName })
      break
    case 'completed':
      meta('CompleteRegistration', { content_name: assessmentName, value: score })
      tiktok('CompleteRegistration', { content_name: assessmentName })
      linkedin()
      break
    case 'started':
      meta('StartTrial', { content_name: assessmentName }, true)
      tiktok('ViewContent', { content_name: assessmentName })
      break
  }
}

export function trackFormSubmit(formName: string, value?: number) {
  ga4('form_submit', { form_name: formName, value })
  meta('Lead', { content_name: formName, value })
  tiktok('SubmitForm', { content_name: formName, value })
  linkedin()
}

export function trackChatInteraction(action: 'opened' | 'message_sent' | 'lead_captured') {
  ga4(`chat_${action}`)
  if (action === 'lead_captured') {
    meta('Contact')
    tiktok('Contact')
  }
}

export function trackMasterclassEvent(action: 'hub_unlocked' | 'resource_click', resourceName?: string) {
  ga4(`masterclass_${action}`, { resource: resourceName })
  meta('ViewContent', { content_name: resourceName || action }, action === 'hub_unlocked')
  tiktok('ViewContent', { content_name: resourceName || action })
}

/**
 * Identify a contact in GHL via client-side API call.
 * Posts to /api/ghl/contact which handles the server-side GHL API call.
 */
export async function identifyGHLContact(
  email: string,
  name?: string,
  phone?: string,
  tags?: string[],
  source?: string
) {
  try {
    await fetch('/api/ghl/contact', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, name, phone, tags, source }),
    })
  } catch {
    // Silently fail — tracking should never break the user experience
  }
}
